import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { comparisonAPI, formatApiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Eye, ArrowLeft, ArrowDown, ArrowUp, Minus, ChevronDown, Loader2, Settings, LogOut, AlertTriangle, TrendingDown, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <header className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-md border-b border-zinc-200">
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
        <Link to="/dashboard" className="flex items-center gap-2">
          <Eye className="w-6 h-6 text-brand" strokeWidth={2} />
          <span className="font-heading font-black text-lg tracking-tight text-zinc-900">ContractEye</span>
        </Link>
        <div className="flex items-center gap-4">
          <span className="font-body text-sm text-zinc-500 hidden sm:block">{user?.email}</span>
          <Link to="/settings" className="p-2 hover:bg-zinc-100"><Settings className="w-4 h-4 text-zinc-600" /></Link>
          <button onClick={async () => { await logout(); navigate('/'); }} className="p-2 hover:bg-zinc-100"><LogOut className="w-4 h-4 text-zinc-600" /></button>
        </div>
      </div>
    </header>
  );
}

function SeverityBadge({ severity }) {
  const s = severity?.toLowerCase();
  if (s === 'high') return <span className="bg-rose-100 text-rose-700 border border-rose-200 px-2 py-0.5 text-xs font-bold uppercase tracking-wider">High</span>;
  if (s === 'medium') return <span className="bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 text-xs font-bold uppercase tracking-wider">Medium</span>;
  return <span className="bg-emerald-100 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-xs font-bold uppercase tracking-wider">Low</span>;
}

function ScoreChange({ diff }) {
  const { risk_score_original, risk_score_revised, risk_score_change, risk_score_direction } = diff;
  let changeColor, changeIcon, changeLabel;
  if (risk_score_direction === 'improved') {
    changeColor = 'text-emerald-600';
    changeIcon = <TrendingDown className="w-5 h-5" />;
    changeLabel = `${Math.abs(risk_score_change)} points lower`;
  } else if (risk_score_direction === 'worsened') {
    changeColor = 'text-rose-600';
    changeIcon = <TrendingUp className="w-5 h-5" />;
    changeLabel = `${Math.abs(risk_score_change)} points higher`;
  } else {
    changeColor = 'text-zinc-500';
    changeIcon = <Minus className="w-5 h-5" />;
    changeLabel = 'No change';
  }

  const scoreColor = (s) => s >= 7 ? 'text-rose-600' : s >= 4 ? 'text-amber-600' : 'text-emerald-600';

  return (
    <div className="bg-white border border-zinc-200 p-6" data-testid="score-change">
      <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 font-body mb-4">Risk Score Change</p>
      <div className="flex items-center justify-center gap-6">
        <div className="text-center">
          <p className="font-body text-xs text-zinc-400 mb-1">Original</p>
          <span className={`font-mono font-bold text-4xl ${scoreColor(risk_score_original)}`}>{risk_score_original}</span>
          <span className="font-mono text-sm text-zinc-300">/10</span>
        </div>
        <div className={`flex flex-col items-center ${changeColor}`}>
          {changeIcon}
          <span className="font-body text-xs font-medium mt-1">{changeLabel}</span>
        </div>
        <div className="text-center">
          <p className="font-body text-xs text-zinc-400 mb-1">Revised</p>
          <span className={`font-mono font-bold text-4xl ${scoreColor(risk_score_revised)}`}>{risk_score_revised}</span>
          <span className="font-mono text-sm text-zinc-300">/10</span>
        </div>
      </div>
    </div>
  );
}

function CollapsibleSection({ title, children, defaultOpen = true, badge }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-zinc-200 bg-white">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-6 py-4 hover:bg-zinc-50 transition-colors">
        <div className="flex items-center gap-2">
          <span className="font-heading font-bold text-sm text-zinc-900">{title}</span>
          {badge}
        </div>
        <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-6 pb-6 border-t border-zinc-100">{children}</div>}
    </div>
  );
}

const clauseLabels = {
  payment_terms: 'Payment Terms', termination: 'Termination', liability: 'Liability',
  indemnity: 'Indemnity', confidentiality: 'Confidentiality', exclusivity: 'Exclusivity',
  renewal: 'Renewal', governing_law: 'Governing Law', dispute_resolution: 'Dispute Resolution',
  ip_ownership: 'IP Ownership', non_compete: 'Non-Compete',
};

export default function ComparisonResultPage() {
  const { comparisonId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await comparisonAPI.get(comparisonId);
        setData(res.data.comparison);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [comparisonId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-brand animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-zinc-50">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <div className="text-center">
            <p className="font-body text-sm text-rose-600 mb-4">Comparison not found</p>
            <Link to="/dashboard" className="text-brand font-body text-sm underline">Back to Dashboard</Link>
          </div>
        </div>
      </div>
    );
  }

  const { original_report: orig, revised_report: rev, diff } = data;

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="comparison-result-page">
      <Navbar />
      <main className="pt-24 pb-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <Link to="/dashboard" className="flex items-center gap-1 font-body text-sm text-zinc-500 hover:text-zinc-700 mb-6" data-testid="comparison-back">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>

          <div className="bg-amber-50 border border-amber-200 px-4 py-3 mb-6">
            <p className="font-body text-xs text-amber-800"><strong>Disclaimer:</strong> This comparison provides risk insights only and does not constitute legal advice.</p>
          </div>

          {/* Header */}
          <div className="bg-white border border-zinc-200 p-6 mb-6" data-testid="comparison-header">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 font-body mb-2">Contract Comparison</p>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-heading font-bold text-lg text-zinc-900">{data.original_title}</span>
              <span className="font-body text-sm text-zinc-400">vs</span>
              <span className="font-heading font-bold text-lg text-zinc-900">{data.revised_title}</span>
            </div>
            <p className="font-body text-xs text-zinc-400 mt-1">{new Date(data.created_at).toLocaleString()}</p>
          </div>

          {/* Score Change */}
          <ScoreChange diff={diff} />

          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200 mt-6 mb-6" data-testid="comparison-stats">
            <div className="bg-white p-4 text-center">
              <p className="font-mono text-2xl font-bold text-zinc-900">{diff.original_flag_count}</p>
              <p className="font-body text-xs text-zinc-500">Original Flags</p>
            </div>
            <div className="bg-white p-4 text-center">
              <p className="font-mono text-2xl font-bold text-zinc-900">{diff.revised_flag_count}</p>
              <p className="font-body text-xs text-zinc-500">Revised Flags</p>
            </div>
            <div className="bg-white p-4 text-center">
              <p className="font-mono text-2xl font-bold text-emerald-600">{diff.flags_removed.length}</p>
              <p className="font-body text-xs text-zinc-500">Flags Resolved</p>
            </div>
            <div className="bg-white p-4 text-center">
              <p className="font-mono text-2xl font-bold text-rose-600">{diff.flags_added.length}</p>
              <p className="font-body text-xs text-zinc-500">New Flags</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Flags Removed (Improvements) */}
            {diff.flags_removed.length > 0 && (
              <CollapsibleSection title="Risks Resolved" badge={<span className="bg-emerald-100 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-xs font-bold">{diff.flags_removed.length}</span>}>
                <div className="space-y-3 mt-2" data-testid="flags-removed">
                  {diff.flags_removed.map((f, i) => (
                    <div key={i} className="border border-emerald-100 bg-emerald-50/50 p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 text-xs font-bold uppercase tracking-wider">Resolved</span>
                        <span className="font-heading font-bold text-sm text-zinc-900">{f.category}</span>
                      </div>
                      <p className="font-body text-sm text-zinc-600">{f.concern}</p>
                    </div>
                  ))}
                </div>
              </CollapsibleSection>
            )}

            {/* New Flags */}
            {diff.flags_added.length > 0 && (
              <CollapsibleSection title="New Risks" badge={<span className="bg-rose-100 text-rose-700 border border-rose-200 px-2 py-0.5 text-xs font-bold">{diff.flags_added.length}</span>}>
                <div className="space-y-3 mt-2" data-testid="flags-added">
                  {diff.flags_added.map((f, i) => (
                    <div key={i} className="border border-rose-100 bg-rose-50/50 p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <SeverityBadge severity={f.severity} />
                        <span className="font-heading font-bold text-sm text-zinc-900">{f.category}</span>
                      </div>
                      <p className="font-body text-sm text-zinc-600">{f.concern}</p>
                    </div>
                  ))}
                </div>
              </CollapsibleSection>
            )}

            {/* Severity Changes */}
            {diff.severity_changes.length > 0 && (
              <CollapsibleSection title="Severity Changes" badge={<span className="bg-blue-100 text-blue-700 border border-blue-200 px-2 py-0.5 text-xs font-bold">{diff.severity_changes.length}</span>}>
                <div className="space-y-3 mt-2" data-testid="severity-changes">
                  {diff.severity_changes.map((c, i) => (
                    <div key={i} className="border border-zinc-100 p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-heading font-bold text-sm text-zinc-900">{c.category}</span>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <SeverityBadge severity={c.from_severity} />
                        <ArrowDown className="w-3 h-3 text-zinc-400" />
                        <SeverityBadge severity={c.to_severity} />
                      </div>
                      <div className="grid md:grid-cols-2 gap-3">
                        <div className="bg-zinc-50 p-3"><p className="font-body text-xs text-zinc-400 mb-1">Original</p><p className="font-body text-xs text-zinc-600">{c.original_concern}</p></div>
                        <div className="bg-zinc-50 p-3"><p className="font-body text-xs text-zinc-400 mb-1">Revised</p><p className="font-body text-xs text-zinc-600">{c.revised_concern}</p></div>
                      </div>
                    </div>
                  ))}
                </div>
              </CollapsibleSection>
            )}

            {/* Clause Changes */}
            {diff.clauses_changed.length > 0 && (
              <CollapsibleSection title="Clause Changes" defaultOpen={false} badge={<span className="bg-zinc-100 text-zinc-600 border border-zinc-200 px-2 py-0.5 text-xs font-bold">{diff.clauses_changed.length}</span>}>
                <div className="space-y-3 mt-2" data-testid="clause-changes">
                  {diff.clauses_changed.map((c, i) => (
                    <div key={i} className="border border-zinc-100 p-4">
                      <span className="font-body text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2 block">{clauseLabels[c.clause] || c.clause}</span>
                      <div className="grid md:grid-cols-2 gap-3">
                        <div className={`p-3 ${c.original ? 'bg-rose-50 border border-rose-100' : 'bg-zinc-50'}`}>
                          <p className="font-body text-xs text-zinc-400 mb-1">Original</p>
                          <p className="font-body text-xs text-zinc-700">{c.original || 'Not found'}</p>
                        </div>
                        <div className={`p-3 ${c.revised ? 'bg-emerald-50 border border-emerald-100' : 'bg-zinc-50'}`}>
                          <p className="font-body text-xs text-zinc-400 mb-1">Revised</p>
                          <p className="font-body text-xs text-zinc-700">{c.revised || 'Not found'}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CollapsibleSection>
            )}

            {/* Side-by-Side Summaries */}
            <CollapsibleSection title="Executive Summaries" defaultOpen={false}>
              <div className="grid md:grid-cols-2 gap-4 mt-2" data-testid="side-by-side-summaries">
                <div className="bg-zinc-50 p-4 border border-zinc-100">
                  <p className="font-body text-xs font-bold text-zinc-500 mb-2 uppercase tracking-wider">Original</p>
                  <p className="font-body text-xs text-zinc-700 leading-relaxed">{orig.executive_summary}</p>
                </div>
                <div className="bg-zinc-50 p-4 border border-zinc-100">
                  <p className="font-body text-xs font-bold text-zinc-500 mb-2 uppercase tracking-wider">Revised</p>
                  <p className="font-body text-xs text-zinc-700 leading-relaxed">{rev.executive_summary}</p>
                </div>
              </div>
            </CollapsibleSection>
          </div>

          {/* CTA */}
          <div className="flex justify-center gap-4 mt-8">
            <Link to="/compare" className="border border-zinc-200 bg-white hover:bg-zinc-50 px-6 py-3 font-body font-medium text-sm transition-colors" data-testid="compare-another-btn">Compare Another</Link>
            <Link to="/dashboard" className="bg-brand text-white hover:bg-brand-hover px-6 py-3 font-body font-medium text-sm transition-colors" data-testid="back-to-dashboard-btn">Back to Dashboard</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
